import { useEffect, useState } from "react";
import { Heart, Copy } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const FALLBACK_QUOTE =
  "The people in your circle are your greatest gift. Never stop celebrating them.";

function getDayOfYearHKT(): number {
  const nowHKT = new Date(Date.now() + 8 * 60 * 60 * 1000);
  const start = new Date(Date.UTC(nowHKT.getUTCFullYear(), 0, 0));
  const diff = nowHKT.getTime() - start.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function cleanOldKeys(currentDay: number) {
  const keys = Object.keys(localStorage);
  for (const key of keys) {
    if (key.startsWith("circle_reflection_")) {
      const dayStr = key.replace("circle_reflection_liked_", "").replace("circle_reflection_", "");
      const dayNum = parseInt(dayStr, 10);
      if (!isNaN(dayNum) && dayNum !== currentDay) {
        localStorage.removeItem(key);
      }
    }
  }
}

const DailyReflectionCard = () => {
  const { user } = useAuth();
  const [quote, setQuote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [animating, setAnimating] = useState(false);

  const dayOfYear = getDayOfYearHKT();
  const today = new Date();

  const getYearHKT = () => {
    const nowHKT = new Date(Date.now() + 8 * 60 * 60 * 1000);
    return nowHKT.getUTCFullYear();
  };

  const trackView = (quoteText: string) => {
    if (!user) return;
    const year = getYearHKT();
    supabase.from('reflection_engagement').upsert(
      { user_id: user.id, day_of_year: dayOfYear, year, quote_text: quoteText, viewed_at: new Date().toISOString() },
      { onConflict: 'user_id,day_of_year,year', ignoreDuplicates: true }
    ).then(() => {});
  };

  // Restore liked state from DB
  useEffect(() => {
    if (!user) return;
    const year = getYearHKT();
    supabase
      .from('reflection_engagement')
      .select('liked')
      .eq('user_id', user.id)
      .eq('day_of_year', dayOfYear)
      .eq('year', year)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.liked) {
          setLiked(true);
          localStorage.setItem(`circle_reflection_liked_${dayOfYear}`, "true");
        }
      });
  }, [user, dayOfYear]);

  useEffect(() => {
    cleanOldKeys(dayOfYear);

    const cachedLike = localStorage.getItem(`circle_reflection_liked_${dayOfYear}`);
    if (cachedLike === "true") setLiked(true);

    const cached = localStorage.getItem(`circle_reflection_${dayOfYear}`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed.day === dayOfYear && parsed.quote) {
          setQuote(parsed.quote);
          setLoading(false);
          trackView(parsed.quote);
          return;
        }
      } catch {}
    }

    const fetchQuote = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-daily-reflection", {
          method: "GET",
        });
        if (error) throw error;
        if (data?.quote) {
          setQuote(data.quote);
          trackView(data.quote);
          localStorage.setItem(
            `circle_reflection_${dayOfYear}`,
            JSON.stringify({ quote: data.quote, day: data.day })
          );
        } else {
          setQuote(FALLBACK_QUOTE);
        }
      } catch {
        setQuote(FALLBACK_QUOTE);
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [dayOfYear]);

  const toggleLike = () => {
    const newLiked = !liked;
    setLiked(newLiked);
    setAnimating(true);
    localStorage.setItem(`circle_reflection_liked_${dayOfYear}`, String(newLiked));
    setTimeout(() => setAnimating(false), 150);

    if (user) {
      const year = getYearHKT();
      supabase.from('reflection_engagement').update({
        liked: newLiked,
        liked_at: newLiked ? new Date().toISOString() : null
      }).eq('user_id', user.id).eq('day_of_year', dayOfYear).eq('year', year).then(() => {});
    }
  };

  const handleShare = async () => {
    const text = `"${quote}" 💛 — Dearestly. | dearestly.co`;
    try {
      await navigator.clipboard.writeText(text);
      toast("Copied — share it with someone 💛", { duration: 2500 });
    } catch {
      toast("Copied — share it with someone 💛", { duration: 2500 });
    }
  };

  return (
    <div
      className="relative mb-4 w-full overflow-hidden rounded-2xl p-6 shadow-sm"
      style={{ backgroundColor: "#FAF6EC" }}
    >
      {/* Decorative gold quarter-arc */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute -right-6 -top-6 h-32 w-32 opacity-40"
        viewBox="0 0 100 100"
        fill="none"
      >
        <path
          d="M 100 0 A 100 100 0 0 0 0 100"
          stroke="#C9A84C"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          d="M 100 20 A 80 80 0 0 0 20 100"
          stroke="#C9A84C"
          strokeWidth="1"
          fill="none"
          opacity="0.6"
        />
      </svg>

      <div className="relative">
        {/* Eyebrow */}
        <p
          className="text-eyebrow"
          style={{ color: "#C9A84C" }}
        >
          Today's thought
        </p>

        {/* Quote */}
        {loading ? (
          <Skeleton className="mt-4 h-20 w-full rounded-lg" />
        ) : (
          <p className="mt-4 font-serif text-xl italic leading-relaxed text-foreground md:text-2xl">
            "{quote}"
          </p>
        )}

        {/* Footer row */}
        {!loading && (
          <div className="mt-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <button
                onClick={toggleLike}
                className="flex items-center gap-1.5 border-0 bg-transparent p-0"
                aria-label="Like"
              >
                <Heart
                  className={`h-4 w-4 transition-transform duration-150 ${
                    animating ? "scale-110" : "scale-100"
                  }`}
                  style={{ color: liked ? "#C9A84C" : "#9CA3AF" }}
                  fill={liked ? "#C9A84C" : "none"}
                />
                <span
                  className="text-sm"
                  style={{ color: liked ? "#C9A84C" : "#9CA3AF" }}
                >
                  {liked ? "Liked" : "Like"}
                </span>
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 border-0 bg-transparent p-0"
                aria-label="Share"
              >
                <Copy className="h-4 w-4" style={{ color: "#9CA3AF" }} />
                <span className="text-sm" style={{ color: "#9CA3AF" }}>
                  Share
                </span>
              </button>
            </div>
            <span className="text-xs" style={{ color: "#9CA3AF" }}>
              Day {dayOfYear} of 365
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyReflectionCard;
